import React, { Dispatch, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { RootState, actions } from "../../store";
import { ThunkDispatch } from "redux-thunk";
import { Action, AnyAction } from "redux";
import { chevronBack } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import WebServerApi from "../api/WebServerApi";
import {
  IonModal,
  IonContent,
  IonHeader,
  IonButtons,
  IonTitle,
  IonToolbar,
  IonIcon,
  IonFooter,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonSpinner,
} from "@ionic/react";
import "./TariffsModal.scss";
import { IGetAddressItem } from "sedi-webserverproxy";

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> & {
    showModal: boolean;
    openModal: () => void;
    tariffTitle: string;
    basicTariffId: string;
    tariffCost: number;
  };

export const TariffsModal: React.FC<Props> = (props) => {
  const {
    showModal,
    openModal,
    tariffTitle,
    activationKeyResult,
    route,
    basicTariffId,
    successAddOrderAction,
    lang,
    tariffCost,
  } = props;
  
  const { t } = useTranslation();
  const webServerApi = useMemo(() => new WebServerApi(), []);
  const [value, setValue] = useState(activationKeyResult?.userPhone);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Оптимизированная функция для форматирования адреса
  const routePoint = useCallback((point: IGetAddressItem | null): string => {
    if (!point) return "";
    
    switch (point.t) {
      case "c":
      case "s":
      case "o":
        return point.v || "";
      default:
        return point.v ? `${point.s}, ${point.v}` : point.s;
    }
  }, []);

  // Вспомогательная функция для подготовки данных адреса
  const prepareAddressData = useCallback((point: IGetAddressItem) => ({
    city: point.c,
    street: point.s,
    house: point.t === "h" ? point.v : point.h || "",
    object: point.t === "o" ? point.v : "",
    lat: point.g.lat,
    lon: point.g.lon,
  }), []);

  const saveClick = useCallback(async () => {
    if (!activationKeyResult?.userKey || !route.point1 || !route.point2) {
      setMessage(t("error.missingData"));
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const data = await webServerApi.addOrder(
        activationKeyResult.userKey,
        activationKeyResult.userPhone,
        lang,
        basicTariffId,
        tariffCost,
        prepareAddressData(route.point1),
        prepareAddressData(route.point2)
      );
      
      setMessage(data.Message);
      successAddOrderAction(data.Success);
    } catch (error) {
      console.error("Order creation failed:", error);
      setMessage(t("error.orderFailed"));
    } finally {
      setLoading(false);
    }
  }, [
    activationKeyResult,
    route.point1,
    route.point2,
    webServerApi,
    lang,
    basicTariffId,
    tariffCost,
    successAddOrderAction,
    t,
    prepareAddressData
  ]);

  const clearClick = useCallback(() => {
    openModal();
    setMessage("");
  }, [openModal]);

  const handlePhoneChange = useCallback((e: CustomEvent) => {
    setValue(e.detail.value || "");
  }, []);

  // Мемоизированные значения для оптимизации рендера
  const point1Address = useMemo(() => routePoint(route.point1), [routePoint, route.point1]);
  const point2Address = useMemo(() => routePoint(route.point2), [routePoint, route.point2]);
  const phoneValue = useMemo(() => 
    value || activationKeyResult?.userPhone || "", 
    [value, activationKeyResult?.userPhone]
  );

  const canSubmit = useMemo(() => 
    activationKeyResult?.userKey && route.point1 && route.point2 && !loading,
    [activationKeyResult?.userKey, route.point1, route.point2, loading]
  );

  return (
    <IonModal isOpen={showModal}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{tariffTitle}</IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={openModal}>
              <IonIcon icon={chevronBack} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      
      <IonContent className="tariffContent">
        <div className="tariffContent__item">
          <span className="inputIcon">A</span>
          <IonLabel>{point1Address}</IonLabel>
        </div>
        
        {route.point2 && (
          <div className="tariffContent__item">
            <span className="inputIcon">B</span>
            <IonLabel>{point2Address}</IonLabel>
          </div>
        )}
        
        <IonList lines="full" className="tariffContent__phone">
          <IonItem>
            <IonLabel position="stacked">{t("tariff.phone")}</IonLabel>
            <IonInput
              required
              type="tel"
              value={phoneValue}
              onIonInput={handlePhoneChange}
              placeholder={t("tariff.phonePlaceholder")}
            />
          </IonItem>
        </IonList>
        
        <div className="tariffContent__message">
          {loading && (
            <div className="loading-container">
              <IonSpinner name="lines" color="primary" />
              <IonLabel>{t("loading")}</IonLabel>
            </div>
          )}
          
          {message && (
            <div className="messageSuccess">
              <p>{message}</p>
              <IonButton color="success" onClick={clearClick}>
                {t("ok")}
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>
      
      <IonFooter className="servicesFooter">
        <IonButton color="medium" onClick={clearClick}>
          {t("cancel")}
        </IonButton>
        <IonButton 
          onClick={saveClick} 
          disabled={!canSubmit}
        >
          {t("order")}
        </IonButton>
      </IonFooter>
    </IonModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  tariffTitle: state.map.tariffTitle,
  valueFromView: state.map.valueFromView,
  activationKeyResult: state.registration.activationKeyResult,
  route: state.map.route,
  lang: state.map.lang,
});

const mapDispatchToProps = (
  dispatch: Dispatch<Action> & ThunkDispatch<any, any, AnyAction>
) => ({
  successAddOrderAction: (successAddOrder: boolean) =>
    dispatch(actions.map.successAddOrderAction(successAddOrder)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TariffsModal);